'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAdpopcornSspPlacementId } from '@/app/(allowed)/benefit/_hooks/useAdpopcornSspPlacementId';
import { useBenefitGate } from '@/app/(allowed)/benefit/_hooks/useBenefitGate';
import { resolveAdTriggerNotice } from '@/app/(allowed)/benefit/_utils/adTriggerNotice';
import { useLuckyTicketCount } from '@/app/(allowed)/benefit/lucky-ticket/_hooks/useLuckyTicketCount';
import { Image, PageNavigation } from '@/components/client';
import { CenteredDetailTemplate } from '@/components/template/CenteredDetailTemplate';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { type AdTriggerPhase, useAdpopcornSspFullscreenAd } from '@/modules/adpopcorn-ssp';
import { ADPOPCORN_SSP_MODULE } from '@/modules/wello-app';
import { type ScratchSessionData, useScratch } from './_hooks/useScratch';
import { resolveScratchResultActions } from './_utils/resolveScratchResultActions';

//* 전면광고가 웹뷰를 덮은 순간(onOpened) 'revealing'으로 미리 전환해, 광고가 닫힐 때
//* 이전(start) 화면이 한 프레임도 보이지 않게 한다. 'result'는 지급 응답 데이터를 가진
//* 상태로만 진입 가능해 거짓 값(0포인트 축하)이 렌더될 수 없다.
type ScratchView = {
  step: 'start';
} | {
  step: 'revealing';
} | {
  step: 'result';
  session: ScratchSessionData;
};
const PARTICIPATE_BADGE = <Badge className="bg-O-25 text-O-600">참여가능</Badge>;

//* 확인 중(폴백)·참여가능·없음 3상태를 전부 Badge로 맞춰 판정 순간 높이가 흔들리지 않게 한다.
const NO_TICKET_BADGE = <Badge className="bg-Gray-100 text-Gray-500">
    열 수 있는 행운권이 없어요
  </Badge>;
const ScratchAvailability = () => {
  const {
    data: luckyTicketCount
  } = useLuckyTicketCount();
  return luckyTicketCount > 0 ? PARTICIPATE_BADGE : NO_TICKET_BADGE;
};
interface ScratchStartFooterViewProps {
  notice: string;
  disabled?: boolean;
  loading?: boolean;
  onScratchClick?: () => void;
}
const ScratchStartFooterView = t0 => {
  const $ = _c(9);
  const {
    notice,
    disabled,
    loading,
    onScratchClick
  } = t0;
  let t1;
  if ($[0] !== notice) {
    t1 = <p className="text-center text-Gray-500 typo-caption2">{notice}</p>;
    $[0] = notice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== loading || $[4] !== onScratchClick) {
    t2 = <Button className="w-full" disabled={disabled} loading={loading} onClick={onScratchClick}>행운권 열기</Button>;
    $[2] = disabled;
    $[3] = loading;
    $[4] = onScratchClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="flex w-full flex-col items-center gap-8">{t1}{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const ScratchStartFooter = t0 => {
  const $ = _c(8);
  const {
    phase,
    onScratchClick
  } = t0;
  const {
    data: luckyTicketCount
  } = useLuckyTicketCount();
  const hasTicket = luckyTicketCount > 0;
  let t1;
  if ($[0] !== hasTicket || $[1] !== phase) {
    t1 = resolveAdTriggerNotice(phase) ?? (hasTicket ? "\uC5F4\uAE30 \uC804\uC5D0 \uAD11\uACE0\uAC00 \uB098\uC640\uC694" : "\uD589\uC6B4\uAD8C\uC744 \uBAA8\uC73C\uBA74 \uC5F4 \uC218 \uC788\uC5B4\uC694");
    $[0] = hasTicket;
    $[1] = phase;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const notice = t1;
  const t2 = phase === "pending";
  const t3 = !hasTicket || phase === "cooldown";
  let t4;
  if ($[3] !== notice || $[4] !== onScratchClick || $[5] !== t2 || $[6] !== t3) {
    t4 = <ScratchStartFooterView notice={notice} loading={t2} disabled={t3} onScratchClick={onScratchClick} />;
    $[3] = notice;
    $[4] = onScratchClick;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};

//* 지급 확인 중(session 없음)에는 당첨 문구를 아예 렌더하지 않는다 — '-' 플레이스홀더는
//* 값이 채워지며 문구가 바뀌는 게 보이고, onOpened 유실 시엔 광고가 닫힌 뒤 페이드아웃하며
//* 실제로 노출된다. 값·레이아웃이 확정된 프레임부터 컨테이너 opacity가 올라가므로
//* 사용자에겐 완성된 결과가 한 번에 등장한다.
//* aria-live 컨테이너와 폭죽 이미지는 상시 마운트한다 — 라이브 영역은 콘텐츠보다 먼저
//* 존재해야 낭독되고, 이미지는 미리 받아 둬야 등장 시점에 늦게 뜨지 않는다.
const ScratchResultBody = t0 => {
  const $ = _c(7);
  const {
    session
  } = t0;
  let t1;
  if ($[0] !== session) {
    t1 = session ? <><Badge className="bg-O-25 text-O-600">남은 행운권 {session.remainingLuckyTicketCount}장</Badge><h1 className="text-center font-700 text-Gray-900 typo-h5">축하합니다!<br />{session.rewardPoint.toLocaleString()} 웰포인트가 당첨되었습니다</h1></> : null;
    $[0] = session;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="flex w-full flex-col items-center gap-12" aria-live="polite">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Image alt={"\uC6F0\uD3EC\uC778\uD2B8 \uB2F9\uCCA8 \uD3ED\uC8FD"} src="/assets/pngs/benefit-scratch/confetti.png" width={240} height={240} preload={true} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <>{t2}{t3}</>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
interface ScratchResultFooterProps {
  session: ScratchSessionData | null;
  phase: AdTriggerPhase;
  onBackClick: () => void;
  onContinueClick: () => void;
}

//* 안내 줄을 상시 예약하고 revealing(지급 확인 중)·확인만·이어서 열기 세 구성을 같은 골격에 담아
//* 고정 푸터 높이가 상태 전환 내내 변하지 않게 한다(start 푸터와도 등높이).
const ScratchResultFooter = t0 => {
  const $ = _c(23);
  const {
    session,
    phase,
    onBackClick,
    onContinueClick
  } = t0;
  let t1;
  if ($[0] !== session) {
    t1 = session ? resolveScratchResultActions(session.remainingLuckyTicketCount) : "continue-or-back";
    $[0] = session;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resultActions = t1;
  let t2;
  if ($[2] !== phase) {
    t2 = resolveAdTriggerNotice(phase);
    $[2] = phase;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const notice = t2;
  const t3 = !session;
  const t4 = session ? "opacity-100" : "opacity-0";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("flex w-full flex-col items-center gap-8", "transition-opacity duration-300 motion-reduce:transition-none", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = !notice;
  const t7 = !notice;
  let t8;
  if ($[6] !== t7) {
    t8 = cn("text-center text-Gray-500 typo-caption2", {
      invisible: t7
    });
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = notice ?? "\uAD11\uACE0\uB97C \uC900\uBE44\uD558\uACE0 \uC788\uC5B4\uC694";
  let t10;
  if ($[8] !== t6 || $[9] !== t8 || $[10] !== t9) {
    t10 = <p aria-hidden={t6} className={t8}>{t9}</p>;
    $[8] = t6;
    $[9] = t8;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== onBackClick || $[13] !== onContinueClick || $[14] !== phase || $[15] !== resultActions || $[16] !== session) {
    t11 = resultActions === "confirm-only" ? <Button className="w-full" onClick={onBackClick}>확인</Button> : <div className="flex w-full gap-8"><Button className="w-108" theme="sub-line" disabled={!session} onClick={onBackClick}>돌아가기</Button><Button className="flex-1" loading={phase === "pending"} disabled={!session || phase === "cooldown"} onClick={onContinueClick}>이어서 열기</Button></div>;
    $[12] = onBackClick;
    $[13] = onContinueClick;
    $[14] = phase;
    $[15] = resultActions;
    $[16] = session;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t3 || $[21] !== t5) {
    t12 = <div aria-hidden={t3} className={t5}>{t10}{t11}</div>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t3;
    $[21] = t5;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};
const BenefitScratchPage = () => {
  const $ = _c(33);
  const router = useCustomRouter();
  const {
    toast
  } = useToast();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      step: "start"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [view, setView] = useState(t0);
  const {
    mutate: scratch
  } = useScratch();
  const {
    triggerGate,
    gateModals
  } = useBenefitGate();
  const placementId = useAdpopcornSspPlacementId(ADPOPCORN_SSP_MODULE.INTERSTITIAL_VIDEO);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setView({
      step: "revealing"
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== placementId || $[3] !== scratch || $[4] !== toast) {
    t2 = {
      autoShow: true,
      onOpened: t1,
      onClosed: () => {
        setView({
          step: "revealing"
        });
        scratch({
          body: {
            placementId,
            sspType: "INTERSTITIAL_VIDEO"
          }
        }, {
          onSuccess: response => {
            const scratchResult = response.context?.scratch;
            if (scratchResult) {
              setView({
                step: "result",
                session: {
                  rewardPoint: scratchResult.wonPoint,
                  remainingLuckyTicketCount: scratchResult.luckyTicketCount
                }
              });
            } else {
              setView({
                step: "start"
              });
              toast({
                message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
                type: "fail"
              });
            }
          },
          onError: () => {
            setView({
              step: "start"
            });
            toast({
              message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
              type: "fail"
            });
          }
        });
      }
    };
    $[2] = placementId;
    $[3] = scratch;
    $[4] = toast;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    load,
    phase
  } = useAdpopcornSspFullscreenAd(ADPOPCORN_SSP_MODULE.INTERSTITIAL_VIDEO, t2);
  let t3;
  if ($[6] !== load || $[7] !== placementId || $[8] !== triggerGate) {
    t3 = () => triggerGate(["app", "login", "adult", "consent"], () => {
      load(placementId);
    });
    $[6] = load;
    $[7] = placementId;
    $[8] = triggerGate;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleScratchClick = t3;
  let t4;
  if ($[10] !== router) {
    t4 = () => {
      router.push(ROUTES.BENEFIT_LUCKY_TICKET.pathname);
    };
    $[10] = router;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleBackClick = t4;
  let t5;
  if ($[12] !== handleBackClick || $[13] !== handleScratchClick || $[14] !== phase || $[15] !== view.session || $[16] !== view.step) {
    t5 = view.step === "start" ? <ErrorBoundary fallback={<ScratchStartFooterView notice={"\uD589\uC6B4\uAD8C\uC744 \uBAA8\uC73C\uBA74 \uC5F4 \uC218 \uC788\uC5B4\uC694"} disabled={true} />}><Suspense fallback={<ScratchStartFooterView notice={"\uC5F4\uAE30 \uC804\uC5D0 \uAD11\uACE0\uAC00 \uB098\uC640\uC694"} disabled={true} />}><ScratchStartFooter phase={phase} onScratchClick={handleScratchClick} /></Suspense></ErrorBoundary> : <ScratchResultFooter session={view.step === "result" ? view.session : null} phase={phase} onBackClick={handleBackClick} onContinueClick={handleScratchClick} />;
    $[12] = handleBackClick;
    $[13] = handleScratchClick;
    $[14] = phase;
    $[15] = view.session;
    $[16] = view.step;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PageNavigation searchButton={true} kebabButton={true} />;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = view.step === "revealing";
  const t8 = view.step !== "start" && "transition-opacity duration-300 motion-reduce:transition-none";
  const t9 = view.step === "revealing" ? "opacity-0" : "opacity-100";
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = cn("flex flex-1 flex-col items-center justify-center gap-32 px-20", t8, t9);
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== view.session || $[23] !== view.step) {
    t11 = view.step === "start" ? <><div className="flex w-full flex-col items-center gap-12"><ErrorBoundary fallback={NO_TICKET_BADGE}><Suspense fallback={<Badge className="bg-Gray-100 text-Gray-500">확인 중</Badge>}><ScratchAvailability /></Suspense></ErrorBoundary><h1 className="text-center font-700 text-Gray-900 typo-h5">행운권 열고<br />웰포인트 받기</h1></div><Image alt={"\uD589\uC6B4\uC758 \uB124\uC78E\uD074\uB85C\uBC84"} src="/assets/pngs/benefit-scratch/clover.png" width={240} height={240} preload={true} /></> : <ScratchResultBody session={view.step === "result" ? view.session : null} />;
    $[22] = view.session;
    $[23] = view.step;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t7) {
    t12 = <div aria-hidden={t7} className={t10}>{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t7;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== gateModals || $[30] !== t12 || $[31] !== t5) {
    t13 = <CenteredDetailTemplate headerOffsetPage={true} className="break-keep" footerClassName="pb-[calc(12px+var(--safe-area-bottom,0px))]" footer={t5}>{t6}{gateModals}{t12}</CenteredDetailTemplate>;
    $[29] = gateModals;
    $[30] = t12;
    $[31] = t5;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};
export default BenefitScratchPage;